import { Button } from 'antd';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import type { FleetAvailabilityState } from '../hooks/use-fleet-availability';

const UNIT_LABELS = { available: 'Available', on_trip: 'On trip', in_yard: 'In yard', in_workshop: 'In workshop', off_road: 'Off road' } as const;

/** Units (a truck with the trailer it pulls) by truck status, then trailers that are on no truck. */
export function FleetAvailabilityStrip({ state, onRetry }: { state: FleetAvailabilityState; onRetry: () => void }) {
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label="Loading fleet availability" className="flex flex-wrap gap-3">
        {Object.keys(UNIT_LABELS).map((key) => (
          <Skeleton key={key} className="h-14 w-28" />
        ))}
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <div role="alert" className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 text-sm">
        Couldn't load fleet availability.
        <Button size="small" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  const { units, spareTrailers } = state.fleet;

  return (
    <section aria-label="Fleet availability" className="flex flex-wrap gap-3">
      {(Object.keys(UNIT_LABELS) as (keyof typeof UNIT_LABELS)[]).map((status) => (
        <div key={status} className="min-w-28 rounded-lg border border-line bg-surface px-4 py-2.5">
          <div className="text-xl font-semibold text-fg">{units[status]}</div>
          <div className="text-xs text-muted">{UNIT_LABELS[status]}</div>
        </div>
      ))}
      <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-2.5">
        <div className="text-xl font-semibold text-fg">{spareTrailers.available + spareTrailers.onTrip}</div>
        <div className="text-xs text-muted">
          Spare trailers ({spareTrailers.available} free, {spareTrailers.onTrip} on trip)
        </div>
      </div>
    </section>
  );
}
